import { AgGridReact } from 'ag-grid-react';
import TableWrapper from 'components/tables/TableWrapper';
import {
  Children,
  MouseEvent as ReactMouseEvent,
  forwardRef,
  Fragment,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import panic from 'errors/panic';
import ActionIcon from 'components/ActionIcon';
import { useDisclosure } from '@mantine/hooks';
import { ColDef, CsvExportParams, GetRowIdFunc, GridApi } from 'ag-grid-community';
import { isEmpty, isEqual, isNumber, isString, noop, omit, pick } from 'lodash';
import {
  Box,
  Checkbox,
  Divider,
  Flex,
  Group,
  Indicator,
  LoadingOverlay,
  Menu,
  NumberInput,
  Stack,
  Switch,
  TextInput,
  useMantineTheme,
} from '@mantine/core';
import Button from 'components/Button';
import { Link } from 'react-router-dom';
import {
  IconCopy,
  IconFile,
  IconFileExport,
  IconFilterCog,
  IconPencilCog,
  IconPlus,
  IconRefresh,
  IconSearch,
  IconTableColumn,
  IconTableOptions,
  IconTrash,
} from '@tabler/icons-react';
import P3Regular from 'components/typography/P3Regular';
import ExportModal, { DataTableExport, DataTableExportConfig } from 'components/tables/ExportModal';
import { read as readWorkBook, utils as xlsxUtils, writeFile as writeFileWorkBook, write as writeWorkBook } from 'xlsx';
import { nanoid } from 'nanoid';
import { useDashboardLayout } from 'layouts/dashboard/use-dashboard-layout';
import H2Medium from 'components/typography/H2Medium';
import P2Regular from 'components/typography/P2Regular';
import P2Medium from 'components/typography/P2Medium';
import BulkActionsBar from 'components/tables/BulkActionsBar';
import objectHash from 'object-hash';
import { useConfirm } from 'components/modals/message/MessageProvider';
import { usePrompt } from 'hooks/modals/use-prompt';
import { Modal } from '@mantine/core';
import P4Regular from 'components/typography/P4Regular';
import P3Medium from 'components/typography/P3Medium';
import { showNotification } from '@mantine/notifications';
import { useApi } from 'api/api-context';
import { AG_GRID_LOCALE_SK } from 'components/tables/locale';
import { useResponsiveDesign } from 'hooks/use-responsive-design';

const PROFILE_NAME_MAX_LENGTH = 50;
const XLSX_BASE_ROW_HEIGHT_PT = 15;
const EMPTY_PROFILE_COLUMN_ALIASES: Record<string, string> = {};

/** Converts an unknown export failure into the error contract used by async table refs. */
const exportError = (error: unknown) => (error instanceof Error ? error : new Error('Export nie je dostupný.'));

/** Expands a former export column into its current export-only columns. */
const expandProfileExportColumns = (columnIds: string[], replacements: Record<string, string[]> = {}) => {
  const seen = new Set<string>();
  return columnIds.flatMap((columnId) =>
    (replacements[columnId] ?? [columnId]).flatMap((replacement) => {
      if (seen.has(replacement)) return [];
      seen.add(replacement);
      return replacement;
    })
  );
};

/** Keeps a profile filter on its existing column. */
const keepProfileFilterColumnId = (columnId: string) => columnId;

/**
 * Default active-status filter used by discard toggles.
 */
function getActiveStatusFilter() {
  return {
    filterType: 'text',
    type: 'equals',
    filter: '1',
  };
}

/**
 * Updates worksheet row heights for rows containing multiline cell values.
 */
function applyMultilineRowHeights(worksheet: any) {
  if (!worksheet || !worksheet['!ref']) {
    return;
  }

  const range = xlsxUtils.decode_range(worksheet['!ref']);
  const rows: Array<{ hpt?: number }> = worksheet['!rows'] ?? [];

  for (let row = range.s.r; row <= range.e.r; row++) {
    let maxLines = 1;

    for (let col = range.s.c; col <= range.e.c; col++) {
      const address = xlsxUtils.encode_cell({ r: row, c: col });
      const cell = worksheet[address];

      if (!cell || cell.v === undefined || cell.v === null) {
        continue;
      }

      const value = String(cell.v);
      const lines = value.split(/\r\n|\r|\n/).length;

      if (lines > maxLines) {
        maxLines = lines;
      }
    }

    if (maxLines > 1) {
      rows[row] = { ...(rows[row] ?? {}), hpt: XLSX_BASE_ROW_HEIGHT_PT * maxLines };
    }
  }

  worksheet['!rows'] = rows;
}

/**
 * The public interface of the DataTable component accessible via ref.
 */
export interface IDataTablePublic {
  getDataAsCsv: () => string;
  getDataAsXlsx: () => Promise<string>;
}

/**
 * Parameters of the DataTable component.
 */
export interface DataTableProps<TData> {
  action: () => Promise<TData[]>;
  tableId?: string;
  title?: string;
  noRowsMessage?: string;
  afterTitle?: ReactNode;
  columns: ColDef[];
  hideToggleDiscarded?: boolean;
  hideToggleDiscardedInColumnsManager?: boolean;
  discardedFilterField?: string;
  toggleDiscardedLabel?: string;
  addButtonText?: string;
  addButtonTarget?: string;
  addButtonLinkTarget?: '_blank';
  addButtonIcon?: ReactNode;
  /** Actions joined to the standard Add button, separated by a vertical divider. */
  addButtonActions?: ReactNode;
  addButtonLoading?: boolean;
  addButtonDisabled?: boolean;
  onAddButtonClick?: () => void;
  hideAddButton?: boolean;
  dataExport?: DataTableExport<TData>;
  selectedRows?: TData[];
  bulkActions?: ReactNode;
  initialFilters?: Record<string, any>;
  onInitialFiltersApplied?: () => void;
  preserveProfileFilterColumnIds?: string[];
  defaultProfileFilters?: Record<string, any>;
  dataRefreshToken?: any;
  /** Keeps previously loaded rows visible without a loader during token refreshes. */
  silentDataRefresh?: boolean;
  suppressSaveProfiles?: boolean;
  suppressProfileChangedNotification?: boolean;
  onReady?: (api: GridApi<TData>) => void;
  onFiltersChange?: (filters: Record<string, any>) => void;
  onSelectedRowsChange?: (rows: TData[]) => void;
  getRowId?: GetRowIdFunc<TData>;
  additionalButtons?: ReactNode;
  profileColumnAliases?: Record<string, string>;
  resolveProfileFilterColumnId?: (columnId: string, filterModel: any) => string;
  profileMigrationReady?: boolean;
}

type ColId = string;

interface ColSort {
  sort: 'asc' | 'desc';
  sortIndex: number;
}

type DataTableProfileConfigSort = Record<ColId, ColSort>;

interface DataTableProfileConfig {
  search: string;
  filters: Record<string, any>;
  hiddenColumns: string[];
  columnOrder: string[];
  columnSizes: Record<ColId, number>;
  sort: DataTableProfileConfigSort;
  exportConfig?: DataTableExportConfig;
  hash: string;
}

interface DataTableProfile {
  id: string;
  name: string;
  initial: DataTableProfileConfig;
  current: DataTableProfileConfig;
  selected: boolean;
  readOnly: boolean;
}

/**
 * Reconciles a saved profile with the table's current column inventory without changing known-column order.
 */
export function migrateProfileConfigColumns(
  config: DataTableProfileConfig,
  defaultConfig: DataTableProfileConfig,
  aliases: Record<string, string> = {},
  resolveFilterColumnId: (columnId: string, filterModel: any) => string = (columnId) => columnId,
  exportColumnIds: string[] = defaultConfig.columnOrder,
  filterColumnIds: string[] = defaultConfig.columnOrder,
  exportColumnReplacements: Record<string, string[]> = {}
): DataTableProfileConfig {
  /** Replaces aliases while retaining the first occurrence in an ordered list. */
  const migrateOrderedIds = (columnIds: string[]) => {
    const seen = new Set<string>();

    return columnIds.flatMap((columnId) => {
      const migratedColumnId = aliases[columnId] ?? columnId;
      if (seen.has(migratedColumnId)) return [];
      seen.add(migratedColumnId);
      return migratedColumnId;
    });
  };

  /** Replaces aliases in keyed settings, giving former visible columns precedence over auxiliary IDs. */
  const migrateKeyedSettings = <T,>(settings: Record<string, T>) => {
    const migrated: Record<string, T> = {};
    const entries = Object.entries(settings).sort(
      ([left], [right]) => Number(left in aliases) - Number(right in aliases)
    );
    entries.forEach(([columnId, value]) => {
      migrated[aliases[columnId] ?? columnId] = value;
    });
    return migrated;
  };

  /** Replaces aliases in filters without overwriting an existing canonical filter model. */
  const migrateFilters = (filters: Record<string, any>) => {
    const migrated: Record<string, any> = {};
    Object.entries(filters).forEach(([columnId, filterModel]) => {
      const migratedColumnId = resolveFilterColumnId(aliases[columnId] ?? columnId, filterModel);
      if (!(migratedColumnId in migrated)) migrated[migratedColumnId] = filterModel;
    });
    return migrated;
  };

  const availableColumnIds = new Set(defaultConfig.columnOrder);
  const availableFilterColumnIds = new Set(filterColumnIds);
  const aliasedColumnOrder = migrateOrderedIds(config.columnOrder);
  const savedColumnIds = new Set(aliasedColumnOrder);
  const addedColumnIds = defaultConfig.columnOrder.filter((columnId) => !savedColumnIds.has(columnId));
  const columnOrder = [...aliasedColumnOrder.filter((columnId) => availableColumnIds.has(columnId)), ...addedColumnIds];
  const hiddenColumnIds = new Set(
    migrateOrderedIds(config.hiddenColumns).filter((columnId) => availableColumnIds.has(columnId))
  );
  const defaultHiddenColumnIds = new Set(defaultConfig.hiddenColumns);
  const columnSizes = migrateKeyedSettings(config.columnSizes);
  const sort = migrateKeyedSettings(config.sort);
  const filters = migrateFilters(config.filters);

  addedColumnIds.forEach((columnId) => {
    if (defaultHiddenColumnIds.has(columnId)) {
      hiddenColumnIds.add(columnId);
    }
  });

  const migrated = {
    ...config,
    columnOrder,
    hiddenColumns: columnOrder.filter((columnId) => hiddenColumnIds.has(columnId)),
    columnSizes: Object.fromEntries(
      Object.entries(columnSizes).filter(([columnId]) => availableColumnIds.has(columnId))
    ),
    sort: Object.fromEntries(Object.entries(sort).filter(([columnId]) => availableColumnIds.has(columnId))),
    filters: Object.fromEntries(Object.entries(filters).filter(([columnId]) => availableFilterColumnIds.has(columnId))),
    hash: '',
  };
  if (config.exportConfig) {
    const availableExportColumnIds = new Set(exportColumnIds);
    const knownExportOrder = expandProfileExportColumns(
      migrateOrderedIds(config.exportConfig.columnOrder),
      exportColumnReplacements
    ).filter((columnId) => availableExportColumnIds.has(columnId));
    const selectedColumns = new Set(
      expandProfileExportColumns(
        config.exportConfig.selectedColumns.map((columnId) => aliases[columnId] ?? columnId),
        exportColumnReplacements
      ).filter((columnId) => availableExportColumnIds.has(columnId))
    );
    migrated.exportConfig = {
      ...config.exportConfig,
      columnOrder: [...knownExportOrder, ...exportColumnIds.filter((columnId) => !knownExportOrder.includes(columnId))],
      selectedColumns: exportColumnIds.filter((columnId) => selectedColumns.has(columnId)),
    };
  }
  migrated.hash = objectHash(migrated);

  return migrated;
}

/**
 * Renders data in a table using the specified action to fetch the data.
 */
function DataTable<TData>(
  {
    action,
    tableId = '',
    title = '',
    noRowsMessage = 'Nenájdené žiadne záznamy',
    afterTitle,
    columns,
    hideToggleDiscarded = false,
    hideToggleDiscardedInColumnsManager = false,
    discardedFilterField = 'status',
    toggleDiscardedLabel = '',
    addButtonText,
    addButtonTarget,
    addButtonLinkTarget,
    addButtonIcon = <IconPlus stroke="1.5" size={24} />,
    addButtonActions,
    onAddButtonClick,
    hideAddButton = false,
    addButtonLoading = false,
    addButtonDisabled = false,
    dataExport,
    selectedRows = [],
    bulkActions = [],
    initialFilters = {},
    onInitialFiltersApplied = noop,
    preserveProfileFilterColumnIds = [],
    defaultProfileFilters = {},
    dataRefreshToken = null,
    silentDataRefresh = false,
    suppressSaveProfiles = false,
    suppressProfileChangedNotification = false,
    onReady = noop,
    onFiltersChange = noop,
    onSelectedRowsChange = noop,
    getRowId,
    additionalButtons,
    profileColumnAliases = EMPTY_PROFILE_COLUMN_ALIASES,
    resolveProfileFilterColumnId = keepProfileFilterColumnId,
    profileMigrationReady = true,
  }: DataTableProps<TData>,
  ref: React.ForwardedRef<IDataTablePublic>
) {
  const theme = useMantineTheme();
  const { isMobile, isTablet } = useResponsiveDesign();
  const { padding, mobileHeaderActionsTarget, tableRowHeight, titleHeight } = useDashboardLayout();
  const agGridRef = useRef<AgGridReact<TData>>(null);
  const { confirm } = useConfirm();
  const { getAction } = useApi();
  const prompt = usePrompt();

  const [ready, setReady] = useState<boolean>(false);
  const [loading, { open: startLoading, close: stopLoading }] = useDisclosure(true);
  const hasLoadedData = useRef(false);
  const [fetchingSilently, setFetchingSilently] = useState(false);
  const showDataLoader = loading && !fetchingSilently;
  const [profilesReady, setProfilesReady] = useState<boolean>(false);
  const [profilesError, setProfilesError] = useState<unknown>(null);
  const initialFiltersPending = useRef(false);
  const [reconciledProfileColumnsHash, setReconciledProfileColumnsHash] = useState<string>('');
  const [data, setData] = useState<any[]>([]);
  const [dataError, setDataError] = useState<unknown>(null);
  const [exporting, { open: startExporting, close: stopExporting }] = useDisclosure(false);
  const [columnsManagerOpened, setColumnsManagerOpened] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [columnsManagerHidden, setColumnsManagerHidden] = useState<string[]>([]);
  const [columnsManagerFilters, setColumnsManagerFilters] = useState<Record<string, any>>({});
  const [columnsManagerWidths, setColumnsManagerWidths] = useState<Record<string, number>>({});
  const exportStateRef = useRef<{ ready: boolean; error: unknown }>({ ready: false, error: null });
  const exportWaitersRef = useRef<Array<{ resolve: () => void; reject: (error: Error) => void }>>([]);
  const dataExportRef = useRef(dataExport);
  dataExportRef.current = dataExport;

  const openColumnsManager = useCallback(() => setColumnsManagerOpened(true), []);
  const closeColumnsManager = useCallback(() => setColumnsManagerOpened(false), []);
  const openSearch = useCallback(() => setSearchOpen(true), []);
  const closeSearch = useCallback(() => setSearchOpen(false), []);
  const showDiscardedToggle = !isMobile && !hideToggleDiscarded;

  const handleColumnsManagerTriggerClick = useCallback(
    (event: ReactMouseEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      openColumnsManager();
    },
    [openColumnsManager]
  );

  const showTableTitle = !isMobile && title !== '';
  const showTableControlsRow = !isMobile;
  const showSecondaryRow = isMobile && !!afterTitle;
  const defaultColumnDefinition = useMemo<ColDef>(
    () => ({
      resizable: true,
      wrapHeaderText: true,
      wrapText: true,
    }),
    []
  );

  const columnsWithBulk = useMemo(() => {
    return [
      {
        field: '_bulk',
        headerName: '',
        suppressMovable: true,
        width: 0,
        minWidth: 0,
        maxWidth: 0,
        hide: true,
        colSpan: ({ node }) => (node?.isRowPinned() ? 999 : 0),
      } satisfies ColDef,
      {
        field: '_checkbox',
        headerName: '',
        checkboxSelection: true,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        suppressMovable: true,
        width: 44,
        minWidth: 44,
        maxWidth: 44,
        lockPosition: 'left',
      } satisfies ColDef,
      ...columns.map((column) =>
        column.field === '_actions'
          ? {
              ...column,
              headerComponent: () => (
                <Group w="100%" position="right" pr={4}>
                  {isMobile ? (
                    <></>
                  ) : isTablet ? (
                    <ActionIcon
                      variant="tertiary"
                      size="md"
                      aria-label="Konfigurovať stĺpce"
                      onMouseDown={(event) => event.stopPropagation()}
                      onClick={handleColumnsManagerTriggerClick}
                    >
                      <IconTableColumn stroke={1.5} size={20} />
                    </ActionIcon>
                  ) : (
                    <Button
                      variant="tertiary"
                      size="sm"
                      leftIcon={<IconTableColumn stroke={1.5} size={20} />}
                      onMouseDown={(event) => event.stopPropagation()}
                      onClick={handleColumnsManagerTriggerClick}
                    >
                      Konfigurovať stĺpce
                    </Button>
                  )}
                </Group>
              ),
            }
          : isMobile && column.floatingFilter
          ? {
              ...column,
              floatingFilter: false,
            }
          : column
      ),
    ].map((column) => {
      const isInternalColumn = column.field === '_bulk' || column.field === '_checkbox' || column.field === '_actions';

      return {
        ...column,
        ...(isInternalColumn ? { resizable: false } : {}),
        lockPinned: column.field !== '_actions',
      };
    });
  }, [columns, handleColumnsManagerTriggerClick, isMobile, isTablet]);

  const configurableColumns = useMemo(
    () => columns.filter(({ cellRenderer, field }) => cellRenderer !== undefined && field !== '_actions'),
    [columns]
  );

  const visibleColumnsManagerColumnsCount = useMemo(
    () => configurableColumns.filter((column) => !columnsManagerHidden.includes(column.field!)).length,
    [configurableColumns, columnsManagerHidden]
  );

  const columnsManagerAllChecked =
    configurableColumns.length > 0 && visibleColumnsManagerColumnsCount === configurableColumns.length;
  const columnsManagerIndeterminate =
    visibleColumnsManagerColumnsCount > 0 && visibleColumnsManagerColumnsCount < configurableColumns.length;

  const setAllColumnsManagerRowsChecked = useCallback(
    (checked: boolean) => {
      setColumnsManagerHidden(checked ? [] : configurableColumns.map((column) => column.field!));
    },
    [configurableColumns]
  );

  const isColumnConfigurable = useCallback(
    (field: string) => configurableColumns.some((column) => column.field === field),
    [configurableColumns]
  );

  const [visibleRowsCount, setVisibleRowsCount] = useState<number>(0);

  const defaultConfig = useMemo(() => {
    const filters: Record<string, any> = { ...defaultProfileFilters };

    if (!hideToggleDiscarded) {
      filters[discardedFilterField] = getActiveStatusFilter();
    }

    const config: DataTableProfileConfig = {
      search: '',
      filters,
      hiddenColumns: configurableColumns.filter(({ hide }) => hide).map(({ field }) => field!),
      columnOrder: configurableColumns.map(({ field }) => field!),
      columnSizes: {},
      sort: {},
      hash: '',
    };

    config.hash = objectHash(config);

    return config;
  }, [defaultProfileFilters, configurableColumns, discardedFilterField, hideToggleDiscarded]);

  const profileInventoryHash = useMemo(
    () =>
      objectHash({
        table: defaultConfig.hash,
        exportColumns: dataExport?.columnKeys ?? [],
        exportColumnReplacements: dataExport?.columnReplacements ?? {},
      }),
    [dataExport?.columnKeys, dataExport?.columnReplacements, defaultConfig.hash]
  );
  const programmaticExportError = dataExport?.error ?? dataError ?? profilesError;
  const programmaticExportReady =
    ready &&
    !loading &&
    profilesReady &&
    profileMigrationReady &&
    reconciledProfileColumnsHash === profileInventoryHash &&
    dataExport?.ready !== false &&
    !programmaticExportError;
  exportStateRef.current = { ready: programmaticExportReady, error: programmaticExportError };

  const [profilesRaw, setProfiles] = useState<DataTableProfile[]>(() => [
    {
      id: nanoid(),
      name: 'Predvolený',
      initial: defaultConfig,
      current: defaultConfig,
      selected: true,
      readOnly: true,
    },
  ]);

  const profiles = useMemo(
    () => profilesRaw.map((profile) => ({ ...profile, modified: profile.current.hash !== profile.initial.hash })),
    [profilesRaw]
  );

  const getProfile = useCallback((id: string) => profiles.find((profile) => profile.id === id), [profiles]);

  const currentProfile = useMemo(() => profiles.find(({ selected }) => selected) ?? profiles[0], [profiles]);

  const visibleProfileColumnKeys = useMemo(() => {
    const configurableColumnIds = new Set(configurableColumns.map(({ field }) => field!));
    const hiddenColumnIds = new Set(currentProfile.current.hiddenColumns);
    const profileOrder = new Map(currentProfile.current.columnOrder.map((columnId, index) => [columnId, index]));
    const orderedConfigurableColumns = [...configurableColumns].sort((a, b) => {
      const indexA = profileOrder.get(a.field!);
      const indexB = profileOrder.get(b.field!);

      return (indexA ?? Number.MAX_SAFE_INTEGER) - (indexB ?? Number.MAX_SAFE_INTEGER);
    });
    let configurableIndex = 0;

    return columns
      .map((column) =>
        configurableColumnIds.has(column.field!) ? orderedConfigurableColumns[configurableIndex++] : column
      )
      .filter(
        ({ field, hide }) =>
          field && field !== '_actions' && (configurableColumnIds.has(field) ? !hiddenColumnIds.has(field) : !hide)
      )
      .map(({ field }) => field!);
  }, [columns, configurableColumns, currentProfile.current.columnOrder, currentProfile.current.hiddenColumns]);

  const sortedConfigurableColumns = useMemo(() => {
    const profileOrder = currentProfile.current.columnOrder;

    /** Keeps profile-known columns ordered while appending newly introduced columns. */
    const sortByProfileOrder = (a: ColDef, b: ColDef) => {
      const indexA = profileOrder.indexOf(a.field!);
      const indexB = profileOrder.indexOf(b.field!);

      return (indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA) - (indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB);
    };

    return [...configurableColumns].sort(sortByProfileOrder);
  }, [configurableColumns, currentProfile.current.columnOrder]);

  const applyProfile = useCallback(
    ({ filters, search, hiddenColumns, columnOrder, columnSizes, sort }: DataTableProfileConfig) => {
      agGridRef.current?.api?.setFilterModel(filters);
      agGridRef.current?.api?.setQuickFilter(search);

      const hiddenColumnIds = new Set(hiddenColumns);
      agGridRef.current?.columnApi.applyColumnState({
        state: configurableColumns.map((column) => ({
          colId: column.field!,
          hide: hiddenColumnIds.has(column.field!),
        })),
      });

      agGridRef.current?.api.sizeColumnsToFit();

      const configurableColumnIds = new Set(configurableColumns.map((column) => column.field!));
      const columnState = agGridRef.current?.columnApi.getColumnState().map((column) => ({
        ...column,
        sort: sort[column.colId]?.sort ?? null,
        sortIndex: sort[column.colId]?.sortIndex ?? null,
        width: columnSizes[column.colId] ?? column.width,
      }));

      const leadingInternalColumnIds = new Set(['_bulk', '_checkbox']);
      const leadingInternalColumnState = columnState?.filter(({ colId }) => leadingInternalColumnIds.has(colId));
      const profileOrder = new Map(columnOrder.map((columnId, index) => [columnId, index]));

      /** Keeps profile-known column states ordered while appending newly introduced states. */
      const sortByProfileOrder = (a: { colId: string }, b: { colId: string }) => {
        const indexA = profileOrder.get(a.colId);
        const indexB = profileOrder.get(b.colId);

        return (indexA ?? Number.MAX_SAFE_INTEGER) - (indexB ?? Number.MAX_SAFE_INTEGER);
      };
      const configurableColumnState = (columnState ?? [])
        .filter(({ colId }) => configurableColumnIds.has(colId))
        .sort(sortByProfileOrder);
      let configurableIndex = 0;
      const orderedColumnState = [
        ...(leadingInternalColumnState ?? []),
        ...(columnState ?? [])
          .filter(({ colId }) => !leadingInternalColumnIds.has(colId))
          .map((column) =>
            configurableColumnIds.has(column.colId) ? configurableColumnState[configurableIndex++] : column
          ),
      ];

      agGridRef.current?.columnApi.applyColumnState({ state: orderedColumnState, applyOrder: true });
    },
    [configurableColumns]
  );

  const switchProfile = useCallback(
    (id: string) => setProfiles((profiles) => profiles.map((profile) => ({ ...profile, selected: profile.id === id }))),
    [setProfiles]
  );

  const updateProfile = useCallback(
    (update: (profile: DataTableProfileConfig) => DataTableProfileConfig) =>
      setProfiles((profiles) =>
        profiles.map((profile) => {
          if (!profile.selected) {
            return profile;
          }

          const current = update(profile.current);
          current.hash = objectHash({ ...current, hash: '' });

          return { ...profile, current };
        })
      ),
    []
  );

  const setSearch = useCallback(
    (search: string) => updateProfile((profile) => ({ ...profile, search })),
    [updateProfile]
  );

  const setFilters = useCallback(
    (filters: Record<string, any>) => updateProfile((profile) => ({ ...profile, filters })),
    [updateProfile]
  );

  const setDiscardedVisibility = useCallback(
    (checked: boolean) => {
      setFilters(
        checked
          ? omit(currentProfile.current.filters, [discardedFilterField])
          : {
              ...currentProfile.current.filters,
              [discardedFilterField]: getActiveStatusFilter(),
            }
      );
    },
    [currentProfile.current.filters, discardedFilterField, setFilters]
  );

  const setColumnSizes = useCallback(
    (columnSizes: Record<ColId, number>) => updateProfile((profile) => ({ ...profile, columnSizes })),
    [updateProfile]
  );

  const setColumnWidth = useCallback(
    (colId: ColId, width: number) =>
      updateProfile((profile) => ({
        ...profile,
        columnSizes: { ...profile.columnSizes, [colId]: width },
      })),
    [updateProfile]
  );

  const autoColumnWidth = useCallback(
    (colId: ColId) => updateProfile((profile) => ({ ...profile, columnSizes: omit(profile.columnSizes, [colId]) })),
    [updateProfile]
  );

  const setHiddenColumns = useCallback(
    (hiddenColumns: string[]) => updateProfile((profile) => ({ ...profile, hiddenColumns })),
    [updateProfile]
  );

  const setColumnOrder = useCallback(
    (columnOrder: string[]) => updateProfile((profile) => ({ ...profile, columnOrder })),
    [updateProfile]
  );

  const setSort = useCallback(
    (sort: DataTableProfileConfigSort) => updateProfile((profile) => ({ ...profile, sort })),
    [updateProfile]
  );

  const setExportConfig = useCallback(
    (exportConfig: DataTableExportConfig | undefined) =>
      updateProfile((profile) =>
        exportConfig === undefined
          ? (omit(profile, ['exportConfig']) as unknown as DataTableProfileConfig)
          : { ...profile, exportConfig }
      ),
    [updateProfile]
  );

  const saveProfile = useCallback(
    () =>
      setProfiles((profiles) =>
        profiles.map((profile) => (profile.selected ? { ...profile, initial: profile.current } : profile))
      ),
    []
  );

  const resetProfile = useCallback(
    () =>
      setProfiles((profiles) =>
        profiles.map((profile) => (profile.selected ? { ...profile, current: profile.initial } : profile))
      ),
    []
  );

  const createEmptyProfile = useCallback(
    (profileName: string) => {
      const config: DataTableProfileConfig = {
        ...defaultConfig,
        filters: { ...defaultConfig.filters },
        hiddenColumns: [...defaultConfig.hiddenColumns],
        columnOrder: [...defaultConfig.columnOrder],
        columnSizes: { ...defaultConfig.columnSizes },
        sort: { ...defaultConfig.sort },
        hash: '',
      };

      config.hash = objectHash(config);

      const profile: DataTableProfile = {
        id: nanoid(),
        name: profileName,
        initial: config,
        current: config,
        selected: true,
        readOnly: false,
      };

      setProfiles((profiles) => [...profiles.map((profile) => ({ ...profile, selected: false })), profile]);
    },
    [defaultConfig]
  );

  const duplicateProfile = useCallback(
    (profileName: string) => {
      const profile: DataTableProfile = {
        ...currentProfile,
        id: nanoid(),
        name: profileName,
        selected: true,
        initial: currentProfile.current,
        readOnly: false,
      };

      setProfiles((profiles) => [...profiles.map((profile) => ({ ...profile, selected: false })), profile]);
    },
    [currentProfile]
  );

  const deleteProfile = useCallback(
    (id: string) =>
      setProfiles((profiles) =>
        profiles.filter((profile) => profile.id !== id).map((profile, index) => ({ ...profile, selected: index === 0 }))
      ),
    []
  );

  const deleteProfileUi = useCallback(
    (id: string) => {
      const profile = getProfile(id);

      if (profile) {
        confirm({
          centered: false,
          title: 'Vymazať profil',
          content: (
            <p>
              Naozaj chcete vymazať profil <b>{profile.name}</b>?
            </p>
          ),
          onConfirm: () => deleteProfile(id),
        });
      }
    },
    [getProfile, confirm, deleteProfile]
  );

  const renameProfile = useCallback(
    (id: string, name: string) =>
      setProfiles((profiles) => profiles.map((profile) => (profile.id === id ? { ...profile, name } : profile))),
    []
  );

  const renameProfileUi = useCallback(
    (id: string) => {
      const profile = getProfile(id);

      if (profile) {
        prompt({
          title: 'Premenovať profil',
          message: 'Zadajte nový názov profilu',
          initialValue: profile.name,
          maxLength: PROFILE_NAME_MAX_LENGTH,
          onConfirm: (name) => renameProfile(id, name),
        });
      }
    },
    [getProfile, prompt, renameProfile]
  );

  const createEmptyProfileUi = useCallback(
    () =>
      prompt({
        title: 'Nový profil',
        message: 'Zadajte názov profilu',
        placeholder: 'Názov profilu',
        maxLength: PROFILE_NAME_MAX_LENGTH,
        onConfirm: (name) => createEmptyProfile(name),
      }),
    [prompt, createEmptyProfile]
  );

  const duplicateProfileUi = useCallback(
    () =>
      prompt({
        title: 'Duplikovať profil',
        message: 'Zadajte názov profilu',
        placeholder: 'Názov profilu',
        initialValue: `${currentProfile.name} (kópia)`,
        maxLength: PROFILE_NAME_MAX_LENGTH,
        onConfirm: (name) => duplicateProfile(name),
      }),
    [prompt, duplicateProfile, currentProfile]
  );

  /**
   * Exports the file as CSV.
   */
  async function exportFile(params: CsvExportParams) {
    const csv = agGridRef.current?.api.getDataAsCsv(params)?.replace(/","(=+)/g, '"," $1');

    if (!csv) {
      return;
    }

    const wb = readWorkBook(csv, {
      type: 'string',
      cellDates: true,
      cellFormula: false,
    });

    const fileName = params.fileName ?? 'export.xlsx';

    const firstSheetName = wb.SheetNames[0];
    if (firstSheetName) {
      applyMultilineRowHeights(wb.Sheets[firstSheetName]);
    }

    writeFileWorkBook(wb, fileName, {
      type: 'file',
      bookType: 'xlsx',
    });
  }

  // Fetch the profiles.
  useEffect(() => {
    let active = true;
    const getProfiles = getAction('UserGetTableProfiles');

    getProfiles({ parameters: { tableId } })
      .then(({ profiles: rawProfiles }) => {
        if (!active) return;
        setProfiles((profiles) => {
          const parsed = (JSON.parse(rawProfiles) ?? profiles) as DataTableProfile[];

          // Apply initial filters to the default profile.
          if (!isEmpty(initialFilters)) {
            const preservedFilters = pick(
              (parsed.find(({ selected }) => selected) ?? parsed[0])?.current.filters,
              preserveProfileFilterColumnIds
            );
            parsed.forEach((profile) => {
              const wasSelected = profile.selected;
              const isDefault = profile.readOnly;

              if (isDefault) {
                const newFilters = {
                  ...defaultConfig.filters,
                  ...preservedFilters,
                  ...initialFilters,
                };
                const hasChanged = !isEqual(newFilters, profile.current.filters);

                profile.selected = true;
                profile.current.filters = newFilters;
                profile.current.hash = objectHash({ ...profile.current, hash: '' });

                if ((hasChanged || !wasSelected) && !suppressProfileChangedNotification) {
                  showNotification({
                    title: 'Aplikované filtre',
                    message: (
                      <p className="my-0">
                        Profil bol zmenený na <b>Predvolený</b> a boli aplikované filtre podľa prekliku.
                      </p>
                    ),
                    color: 'orange',
                  });
                }
              } else {
                profile.selected = false;
              }
            });
          }

          return parsed;
        });
        initialFiltersPending.current = !isEmpty(initialFilters);
      })
      .catch((error) => {
        panic(error);
        if (active) setProfilesError(exportError(error));
      })
      .finally(() => {
        if (active) setProfilesReady(true);
      });
    return () => {
      active = false;
    };
  }, []);

  // Reconcile profiles after hydration and whenever runtime-generated columns change.
  useEffect(() => {
    if (
      !profilesReady ||
      profilesError ||
      !profileMigrationReady ||
      loading ||
      dataError ||
      reconciledProfileColumnsHash === profileInventoryHash
    ) {
      return;
    }

    if (dataExport?.error) {
      setReconciledProfileColumnsHash(profileInventoryHash);
      return;
    }

    setProfiles((profiles) =>
      profiles.map((profile) => ({
        ...profile,
        initial: profile.readOnly
          ? defaultConfig
          : migrateProfileConfigColumns(
              profile.initial,
              defaultConfig,
              profileColumnAliases,
              resolveProfileFilterColumnId,
              dataExport?.columnKeys,
              [...columns.flatMap(({ field }) => (field ? [field] : [])), ...Object.keys(defaultConfig.filters)],
              dataExport?.columnReplacements
            ),
        current: migrateProfileConfigColumns(
          profile.current,
          defaultConfig,
          profileColumnAliases,
          resolveProfileFilterColumnId,
          dataExport?.columnKeys,
          [...columns.flatMap(({ field }) => (field ? [field] : [])), ...Object.keys(defaultConfig.filters)],
          dataExport?.columnReplacements
        ),
      }))
    );
    setReconciledProfileColumnsHash(profileInventoryHash);
  }, [
    dataExport?.columnReplacements,
    dataExport?.columnKeys,
    dataExport?.error,
    columns,
    defaultConfig,
    profileInventoryHash,
    profileColumnAliases,
    profileMigrationReady,
    profilesReady,
    profilesError,
    loading,
    dataError,
    reconciledProfileColumnsHash,
    resolveProfileFilterColumnId,
  ]);

  // Fetch / refresh data.
  useEffect(() => {
    let active = true;
    setFetchingSilently(silentDataRefresh && hasLoadedData.current);
    startLoading();
    setDataError(null);
    action()
      .then((rows) => {
        if (active) {
          hasLoadedData.current = true;
          setData(rows);
        }
      })
      .catch((error) => {
        if (active) {
          setDataError(exportError(error));
          panic(error);
        }
      })
      .finally(() => {
        if (active) stopLoading();
      });
    return () => {
      active = false;
    };
  }, [dataRefreshToken]);

  // Apply the profile on data change.
  useEffect(() => {
    if (profileMigrationReady && reconciledProfileColumnsHash === profileInventoryHash && ready && !loading) {
      applyProfile(currentProfile.current);
      if (initialFiltersPending.current) {
        initialFiltersPending.current = false;
        onInitialFiltersApplied();
      }
    }
  }, [profileMigrationReady, reconciledProfileColumnsHash, profileInventoryHash, ready, loading, currentProfile.id]);

  // Save profiles on change.
  useEffect(() => {
    if (
      profileMigrationReady &&
      reconciledProfileColumnsHash === profileInventoryHash &&
      ready &&
      !loading &&
      !dataError &&
      !profilesError &&
      !suppressSaveProfiles
    ) {
      const saveProfiles = getAction('UserSetTableProfiles');
      saveProfiles({ parameters: { tableId }, payload: { profiles: JSON.stringify(profilesRaw) } }).catch(panic);
    }
  }, [
    profiles,
    profileMigrationReady,
    reconciledProfileColumnsHash,
    profileInventoryHash,
    ready,
    loading,
    dataError,
    profilesError,
    tableId,
    suppressSaveProfiles,
  ]);

  // Apply quick search.
  useEffect(() => {
    if (
      profilesReady &&
      profileMigrationReady &&
      reconciledProfileColumnsHash === profileInventoryHash &&
      ready &&
      !loading
    ) {
      agGridRef.current?.api?.setQuickFilter(currentProfile.current.search);
    }
  }, [
    profilesReady,
    profileMigrationReady,
    reconciledProfileColumnsHash,
    profileInventoryHash,
    ready,
    loading,
    currentProfile.current.search,
  ]);

  // Apply filters.
  useEffect(() => {
    if (
      profilesReady &&
      profileMigrationReady &&
      reconciledProfileColumnsHash === profileInventoryHash &&
      ready &&
      !loading
    ) {
      agGridRef.current?.api?.setFilterModel(currentProfile.current.filters);
    }
  }, [
    profilesReady,
    profileMigrationReady,
    reconciledProfileColumnsHash,
    profileInventoryHash,
    ready,
    loading,
    currentProfile.current.filters,
  ]);

  useEffect(() => {
    setColumnsManagerHidden(currentProfile.current.hiddenColumns);
  }, [currentProfile.current.hiddenColumns]);

  useEffect(() => {
    setColumnsManagerFilters(currentProfile.current.filters);
  }, [currentProfile.current.filters]);

  useEffect(() => {
    setColumnsManagerWidths(currentProfile.current.columnSizes);
  }, [currentProfile.current.columnSizes]);

  useEffect(() => {
    if (!columnsManagerOpened) {
      return;
    }

    setColumnsManagerHidden(currentProfile.current.hiddenColumns);
    setColumnsManagerFilters(currentProfile.current.filters);
    setColumnsManagerWidths(currentProfile.current.columnSizes);
  }, [
    columnsManagerOpened,
    currentProfile.current.columnSizes,
    currentProfile.current.filters,
    currentProfile.current.hiddenColumns,
  ]);

  useEffect(() => {
    if (!programmaticExportReady && !programmaticExportError) return;

    const waiters = exportWaitersRef.current.splice(0);
    if (programmaticExportError) {
      const error = exportError(programmaticExportError);
      waiters.forEach(({ reject }) => reject(error));
    } else {
      waiters.forEach(({ resolve }) => resolve());
    }
  }, [programmaticExportError, programmaticExportReady]);

  useEffect(
    () => () => {
      const error = new Error('Tabuľka bola zatvorená pred dokončením exportu.');
      exportWaitersRef.current.splice(0).forEach(({ reject }) => reject(error));
    },
    []
  );

  // Set the ref.
  useImperativeHandle(ref, () => {
    /** Returns the data as CSV. */
    const getDataAsCsv = () =>
      agGridRef.current?.api.getDataAsCsv(dataExportRef.current)?.replace(/","(=+)/g, '"," $1') ?? '';

    /** Returns the data as XLSX. */
    const getDataAsXlsx = async () => {
      if (exportStateRef.current.error) throw exportError(exportStateRef.current.error);
      if (!exportStateRef.current.ready) {
        await new Promise<void>((resolve, reject) => exportWaitersRef.current.push({ resolve, reject }));
      }

      const csv = getDataAsCsv();

      const wb = readWorkBook(csv, {
        type: 'string',
        cellDates: true,
        cellFormula: false,
      });

      const firstSheetName = wb.SheetNames[0];
      if (firstSheetName) {
        applyMultilineRowHeights(wb.Sheets[firstSheetName]);
      }

      const xlsx = writeWorkBook(wb, {
        type: 'base64',
        bookType: 'xlsx',
      });

      return xlsx;
    };

    return { getDataAsCsv, getDataAsXlsx };
  });

  const hasBulkActions = !!bulkActions && selectedRows.length > 0 && data.length > 0;

  /**
   * Renders the profile-management menu items for the current table profile.
   */
  function renderProfileMenuItems(includeSaveAndReset: boolean) {
    return Children.toArray([
      ...profiles.map(({ id, name, modified }) => (
        <Menu.Item
          key={id}
          aria-current={id === currentProfile.id ? 'true' : undefined}
          onClick={() => switchProfile(id)}
          rightSection={modified && <Box ml={8} w={8} h={8} bg="red.6" style={{ borderRadius: '50%' }} />}
          fw={id === currentProfile.id ? 'bold' : 'normal'}
        >
          {name}
        </Menu.Item>
      )),
      <Menu.Divider key="new-profile-divider" />,
      <Menu.Item key="new-profile" color="red" icon={<IconPlus size={16} />} onClick={createEmptyProfileUi}>
        Nový profil (prázdny)
      </Menu.Item>,
      <Menu.Item key="duplicate-profile" color="red" icon={<IconCopy size={16} />} onClick={duplicateProfileUi}>
        Duplikovať profil
      </Menu.Item>,
      <Menu.Divider key="manage-profile-divider" />,
      <Menu.Item
        key="rename-profile"
        icon={<IconPencilCog size={16} />}
        color="gray"
        disabled={currentProfile.readOnly}
        onClick={() => renameProfileUi(currentProfile.id)}
      >
        Premenovať profil
      </Menu.Item>,
      <Menu.Item key="configure-columns" icon={<IconFilterCog size={16} />} color="gray" onClick={openColumnsManager}>
        Konfigurovať profil
      </Menu.Item>,
      ...(includeSaveAndReset
        ? [
            <Menu.Item
              key="save-profile"
              icon={<IconFile size={16} />}
              color="gray"
              disabled={currentProfile.readOnly}
              onClick={saveProfile}
            >
              Uložiť
            </Menu.Item>,
            <Menu.Item
              key="reset-profile"
              icon={<IconRefresh size={16} />}
              color="gray"
              disabled={!currentProfile.modified}
              onClick={() => {
                applyProfile(currentProfile.initial);
                resetProfile();
              }}
            >
              Obnoviť
            </Menu.Item>,
            <Menu.Divider key="delete-profile-divider" />,
          ]
        : [<Menu.Divider key="delete-profile-divider" />]),
      <Menu.Item
        key="delete-profile"
        icon={<IconTrash size={16} />}
        color="red"
        disabled={currentProfile.readOnly}
        onClick={() => deleteProfileUi(currentProfile.id)}
      >
        Vymazať profil
      </Menu.Item>,
    ]);
  }

  const mobileBreadcrumbActions = useMemo(() => {
    if (!isMobile) {
      return null;
    }

    return (
      <Group spacing={4} noWrap>
        <Menu withinPortal>
          <Menu.Target>
            <ActionIcon variant="tertiary" size="md" aria-label="Tabuľkové profily">
              <Indicator color="red.6" size={8} offset={4} disabled={!currentProfile.modified}>
                <IconTableOptions stroke={1.5} size={20} />
              </Indicator>
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>{renderProfileMenuItems(true)}</Menu.Dropdown>
        </Menu>
        <Box pos="relative">
          <Indicator color="red.6" size={8} offset={4} disabled={!currentProfile.current.search}>
            <ActionIcon variant="tertiary" color="gray" size="md" aria-label="Hľadať v tabuľke" onClick={openSearch}>
              <IconSearch stroke={1.5} size={20} />
            </ActionIcon>
          </Indicator>
          {searchOpen && (
            <TextInput
              autoFocus
              size="md"
              w={260}
              style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', zIndex: 200 }}
              rightSection={<IconSearch stroke={1.5} color="var(--mantine-color-gray-6)" />}
              placeholder="Hľadať v tabuľke"
              value={currentProfile.current.search}
              onChange={(event) => setSearch(event.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') closeSearch();
              }}
              onBlur={closeSearch}
            />
          )}
        </Box>
      </Group>
    );
  }, [
    applyProfile,
    closeSearch,
    createEmptyProfileUi,
    currentProfile.current.search,
    currentProfile.id,
    currentProfile.initial,
    currentProfile.modified,
    currentProfile.name,
    currentProfile.readOnly,
    deleteProfileUi,
    duplicateProfileUi,
    isMobile,
    openColumnsManager,
    openSearch,
    profiles,
    renameProfileUi,
    resetProfile,
    saveProfile,
    searchOpen,
    setDiscardedVisibility,
    setSearch,
    showDiscardedToggle,
    switchProfile,
    toggleDiscardedLabel,
  ]);

  const tableHeaderHeightCompensation = useMemo(() => {
    if (!isMobile) {
      return 0;
    }

    return showSecondaryRow ? titleHeight - 36 : titleHeight;
  }, [isMobile, showSecondaryRow, titleHeight]);

  const addButton = (
    <>
      {!hideAddButton && addButtonTarget && addButtonText && (
        <Button
          variant="primary"
          component={Link}
          to={addButtonTarget}
          target={addButtonLinkTarget}
          rel={addButtonLinkTarget === '_blank' ? 'noopener noreferrer' : undefined}
          size="md"
          leftIcon={addButtonIcon}
          loading={addButtonLoading}
          disabled={addButtonDisabled}
        >
          {addButtonText}
        </Button>
      )}
      {!hideAddButton && onAddButtonClick && addButtonText && (
        <Button
          variant="primary"
          size="md"
          leftIcon={addButtonIcon}
          onClick={onAddButtonClick}
          loading={addButtonLoading}
          disabled={addButtonDisabled}
        >
          {addButtonText}
        </Button>
      )}
    </>
  );

  return (
    <Stack spacing={0}>
      {isMobile &&
        mobileHeaderActionsTarget &&
        mobileBreadcrumbActions &&
        createPortal(mobileBreadcrumbActions, mobileHeaderActionsTarget)}
      {showTableControlsRow && (
        <Group mih={titleHeight} align="center" position="apart" pt={isTablet ? 8 : 0} pb={8} noWrap>
          <Group maw={750} spacing={isTablet ? 8 : 16} noWrap>
            {showTableTitle && <H2Medium>{title}</H2Medium>}
            {showDiscardedToggle && (
              <Switch
                mt={6}
                checked={!currentProfile.current.filters[discardedFilterField]}
                onChange={(event) => setDiscardedVisibility(event.currentTarget.checked)}
                label={toggleDiscardedLabel}
              />
            )}
            {afterTitle}
          </Group>
          <Group spacing={isTablet ? 4 : 16} noWrap>
            <Group spacing={4} noWrap>
              <Menu>
                <Menu.Target>
                  <Button size="sm" variant="tertiary" leftIcon={<IconTableOptions stroke={1.5} size={24} />}>
                    <Group spacing={4}>
                      <P2Regular>Profil:</P2Regular>
                      <P2Medium>{currentProfile.name}</P2Medium>
                      {currentProfile.modified && <Box ml={8} w={8} h={8} bg="red.6" style={{ borderRadius: '50%' }} />}
                    </Group>
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>{renderProfileMenuItems(false)}</Menu.Dropdown>
              </Menu>
              <Button
                size="sm"
                variant="tertiary"
                leftIcon={<IconFile stroke={1.5} />}
                disabled={currentProfile.readOnly}
                onClick={saveProfile}
              >
                Uložiť
              </Button>
              {isTablet ? (
                <ActionIcon variant="tertiary" disabled={!currentProfile.modified} onClick={resetProfile}>
                  <IconRefresh color={theme.colors.red[currentProfile.modified ? 8 : 2]} stroke={1.5} />
                </ActionIcon>
              ) : (
                <Button
                  size="sm"
                  variant="danger-secondary"
                  leftIcon={<IconRefresh stroke={1.5} />}
                  onClick={() => {
                    applyProfile(currentProfile.initial);
                    resetProfile();
                  }}
                  disabled={!currentProfile.modified}
                >
                  Obnoviť
                </Button>
              )}
            </Group>
            {isTablet ? (
              <Box pos="relative">
                <Indicator color="red.6" size={8} offset={4} disabled={!currentProfile.current.search}>
                  <ActionIcon variant="tertiary" color="gray" size="md" onClick={openSearch}>
                    <IconSearch stroke={1.5} size={20} />
                  </ActionIcon>
                </Indicator>
                {searchOpen && (
                  <TextInput
                    autoFocus
                    size="md"
                    w={336}
                    style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', zIndex: 200 }}
                    rightSection={<IconSearch stroke={1.5} color={theme.colors.gray[6]} />}
                    placeholder="Hľadať v tabuľke"
                    value={currentProfile.current.search}
                    onChange={(event) => setSearch(event.currentTarget.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') closeSearch();
                    }}
                    onBlur={closeSearch}
                  />
                )}
              </Box>
            ) : (
              <TextInput
                size="md"
                miw={336}
                rightSection={<IconSearch stroke={1.5} color={theme.colors.gray[6]} />}
                placeholder="Hľadať v tabuľke"
                value={currentProfile.current.search}
                onChange={(event) => setSearch(event.currentTarget.value)}
              />
            )}
          </Group>
        </Group>
      )}
      {showSecondaryRow && (
        <Group mih={36} align="center" position="apart" py={8} noWrap>
          <Group spacing={12} noWrap>
            {afterTitle}
          </Group>
        </Group>
      )}
      <Box pos="relative">
        <LoadingOverlay visible={showDataLoader && data.length > 0} overlayBlur={2} />
        <TableWrapper heightCompensation={tableHeaderHeightCompensation}>
          <AgGridReact
            defaultColDef={defaultColumnDefinition}
            ref={agGridRef}
            rowData={data}
            getRowId={getRowId}
            columnDefs={columnsWithBulk}
            onGridReady={({ api }) => {
              setReady(true);
              onReady(api);
            }}
            onFilterChanged={({ api, source }) => {
              setVisibleRowsCount(api.getDisplayedRowCount());

              if (
                profilesReady &&
                profileMigrationReady &&
                reconciledProfileColumnsHash === profileInventoryHash &&
                ready &&
                !loading &&
                source !== 'quickFilter'
              ) {
                const nextFilters = api.getFilterModel();
                setFilters(nextFilters);
                onFiltersChange(nextFilters);
              }
            }}
            onSortChanged={({ columnApi }) => {
              const sortState: DataTableProfileConfigSort = {};

              columnApi.getColumnState().forEach(({ colId, sort, sortIndex }) => {
                if (isString(sort) && isNumber(sortIndex)) {
                  sortState[colId] = { sort, sortIndex };
                }
              });

              setSort(sortState);
            }}
            onColumnMoved={({ columnApi, source, finished }) => {
              // Only update the profile after a user-initiated column drag finishes.
              // AG Grid emits this event repeatedly while the pointer crosses column
              // boundaries; rerendering from those intermediate events interrupts the drag.
              // When viewVariant (or any other prop) changes, AG Grid resets column order
              // via orderGridColumnsLikePrimary() and fires this event with source
              // 'gridOptionsChanged', which would incorrectly overwrite the saved profile.
              if (!finished || (source !== 'uiColumnDragged' && source !== 'uiColumnMoved')) {
                return;
              }

              const colOrder = columnApi
                .getColumnState()
                .filter(({ colId }) => isColumnConfigurable(colId))
                .map(({ colId }) => colId);

              setColumnOrder(colOrder);
            }}
            onColumnResized={({ column, source }) => {
              if (column) {
                const colId = column.getColId();
                const width = column.getActualWidth();

                if (source === 'autosizeColumns') {
                  autoColumnWidth(colId);
                } else if (source === 'uiColumnResized') {
                  setColumnWidth(colId, width);
                } else {
                  // We don't care about other sources.
                }
              }
            }}
            onModelUpdated={({ api }) => setVisibleRowsCount(api.getDisplayedRowCount())}
            onSelectionChanged={({ api }) => onSelectedRowsChange(api.getSelectedRows())}
            rowHeight={tableRowHeight}
            className="ag-theme-alpine data-table-ag-grid"
            rowSelection="multiple"
            enableCellTextSelection
            suppressCellFocus
            suppressRowClickSelection
            suppressDragLeaveHidesColumns
            alwaysShowHorizontalScroll
            includeHiddenColumnsInQuickFilter
            floatingFiltersHeight={isMobile ? 0 : undefined}
            noRowsOverlayComponent={() => (showDataLoader ? 'Načítavanie ...' : noRowsMessage)}
            noRowsOverlayComponentParams={{ loading: showDataLoader, noRowsMessage }}
            pinnedTopRowData={hasBulkActions ? [data[0]] : []}
            localeText={AG_GRID_LOCALE_SK}
          />
          <Flex
            gap={isMobile ? 8 : 12}
            p={isMobile ? padding : isTablet ? 8 : 16}
            align={isMobile ? 'flex-start' : 'center'}
            h="100%"
            bg="gray.0"
            justify="space-between"
            wrap={isMobile ? 'wrap' : 'nowrap'}
          >
            <Group spacing={isMobile ? 8 : 16} noWrap={!isMobile}>
              {addButtonActions && !hideAddButton && addButtonText && (addButtonTarget || onAddButtonClick) ? (
                <Group
                  spacing={0}
                  noWrap
                  role="group"
                  aria-label={addButtonText}
                  sx={{
                    '& > :first-child': { borderTopRightRadius: 0, borderBottomRightRadius: 0 },
                    '& > :last-child': { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 },
                  }}
                >
                  {addButton}
                  <Divider orientation="vertical" aria-orientation="vertical" color="red.5" />
                  {addButtonActions}
                </Group>
              ) : (
                addButton
              )}
              {dataExport && (
                <>
                  {isMobile ? (
                    <ActionIcon
                      variant="tertiary"
                      size="md"
                      aria-label="Exportovať tabuľku"
                      onClick={startExporting}
                      disabled={dataExport.ready === false || !!dataExport.error}
                    >
                      <IconFileExport stroke="1.5" size={20} />
                    </ActionIcon>
                  ) : (
                    <Button
                      variant="tertiary"
                      size="md"
                      leftIcon={<IconFileExport stroke="1.5" size={24} />}
                      onClick={startExporting}
                      disabled={dataExport.ready === false || !!dataExport.error}
                    >
                      Exportovať
                    </Button>
                  )}
                  <ExportModal
                    agGridRef={agGridRef}
                    columns={columns}
                    dataExport={dataExport}
                    exportConfig={currentProfile.current.exportConfig}
                    visibleColumnKeys={visibleProfileColumnKeys}
                    opened={exporting}
                    onClose={stopExporting}
                    onExport={exportFile}
                    onExportConfigChange={setExportConfig}
                  />
                </>
              )}
              {additionalButtons}
            </Group>
            <P3Regular color="gray.7">Záznamy: {visibleRowsCount}</P3Regular>
          </Flex>
          {hasBulkActions && (
            <BulkActionsBar
              bulkActions={bulkActions}
              selectedRowsCount={selectedRows.length}
              onDeselectAll={() => agGridRef.current?.api.deselectAll()}
            />
          )}
        </TableWrapper>
      </Box>
      {columnsManagerOpened && (
        <Modal
          opened={columnsManagerOpened}
          onClose={closeColumnsManager}
          closeOnClickOutside={false}
          centered
          title="Konfigurácia profilu"
          size={isMobile ? '100%' : 'auto'}
          styles={{
            content: {
              maxWidth: isMobile ? 'calc(100vw - 32px)' : undefined,
            },
            body: {
              overflowX: 'hidden',
            },
          }}
        >
          <Stack spacing={16}>
            {!hideToggleDiscarded && !hideToggleDiscardedInColumnsManager && (
              <Switch
                checked={!columnsManagerFilters[discardedFilterField]}
                onChange={(event) => {
                  const checked = event.currentTarget.checked;
                  setColumnsManagerFilters((filters) =>
                    checked
                      ? omit(filters, [discardedFilterField])
                      : {
                          ...filters,
                          [discardedFilterField]: getActiveStatusFilter(),
                        }
                  );
                }}
                label={toggleDiscardedLabel}
              />
            )}
            {isMobile ? (
              <Stack spacing={12} data-testid="columns-manager-mobile-layout">
                <Checkbox
                  size="md"
                  label={<P2Medium pl={8}>Všetky stĺpce</P2Medium>}
                  checked={columnsManagerAllChecked}
                  indeterminate={columnsManagerIndeterminate}
                  onChange={(e) => setAllColumnsManagerRowsChecked(e.currentTarget.checked)}
                />
                {sortedConfigurableColumns.map((column) => (
                  <Box
                    key={column.field}
                    p={12}
                    sx={(currentTheme) => ({
                      border: `1px solid ${currentTheme.colors.gray[2]}`,
                      borderRadius: '4px',
                    })}
                  >
                    <Stack spacing={12}>
                      <Checkbox
                        size="md"
                        label={<P2Medium pl={8}>{column.headerName}</P2Medium>}
                        checked={!columnsManagerHidden.includes(column.field!)}
                        onChange={(e) => {
                          if (e.currentTarget.checked) {
                            setColumnsManagerHidden(columnsManagerHidden.filter((field) => field !== column.field));
                          } else {
                            setColumnsManagerHidden([...columnsManagerHidden, column.field!]);
                          }
                        }}
                      />
                      {agGridRef.current?.api && column.floatingFilterComponent && (
                        <Stack spacing={6}>
                          <P3Medium c="gray.6">Filter</P3Medium>
                          <Box
                            component={column.floatingFilterComponent}
                            api={agGridRef.current.api}
                            filterModel={columnsManagerFilters}
                            onChange={(filter: any) =>
                              setColumnsManagerFilters((filters) => ({ ...filters, ...filter }))
                            }
                          />
                        </Stack>
                      )}
                      <Stack spacing={6}>
                        <P3Medium c="gray.6">Šírka</P3Medium>
                        <NumberInput
                          aria-label={`Šírka stĺpca ${column.headerName}`}
                          rightSection={<P4Regular>px</P4Regular>}
                          size="sm"
                          min={0}
                          max={999}
                          placeholder="auto"
                          disabled={column.resizable === false}
                          value={
                            column.resizable === false ? column.width ?? '' : columnsManagerWidths[column.field!] ?? ''
                          }
                          onChange={(value) =>
                            setColumnsManagerWidths((widths) =>
                              value === '' ? omit(widths, [column.field!]) : { ...widths, [column.field!]: value }
                            )
                          }
                        />
                      </Stack>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Box py={4} className="grid grid-cols-[auto_250px_80px] items-center gap-x-4 gap-y-2">
                <Checkbox
                  size="md"
                  label={
                    <P3Medium c="gray.6" pl={8}>
                      Stĺpec
                    </P3Medium>
                  }
                  checked={columnsManagerAllChecked}
                  indeterminate={columnsManagerIndeterminate}
                  onChange={(e) => setAllColumnsManagerRowsChecked(e.currentTarget.checked)}
                />
                <P3Medium c="gray.6">Filter</P3Medium>
                <P3Medium c="gray.6">Šírka</P3Medium>
                {sortedConfigurableColumns.map((column) => (
                  <Fragment key={column.field}>
                    <Checkbox
                      size="md"
                      label={<P2Medium pl={8}>{column.headerName}</P2Medium>}
                      checked={!columnsManagerHidden.includes(column.field!)}
                      onChange={(e) => {
                        if (e.currentTarget.checked) {
                          setColumnsManagerHidden(columnsManagerHidden.filter((field) => field !== column.field));
                        } else {
                          setColumnsManagerHidden([...columnsManagerHidden, column.field!]);
                        }
                      }}
                    />
                    {agGridRef.current?.api && column.floatingFilterComponent ? (
                      <Box
                        component={column.floatingFilterComponent}
                        api={agGridRef.current.api}
                        filterModel={columnsManagerFilters}
                        onChange={(filter: any) => setColumnsManagerFilters((filters) => ({ ...filters, ...filter }))}
                      />
                    ) : (
                      <Box aria-hidden />
                    )}
                    <NumberInput
                      aria-label={`Šírka stĺpca ${column.headerName}`}
                      rightSection={<P4Regular>px</P4Regular>}
                      size="sm"
                      min={0}
                      max={999}
                      placeholder="auto"
                      disabled={column.resizable === false}
                      value={
                        column.resizable === false ? column.width ?? '' : columnsManagerWidths[column.field!] ?? ''
                      }
                      onChange={(value) =>
                        setColumnsManagerWidths((widths) =>
                          value === '' ? omit(widths, [column.field!]) : { ...widths, [column.field!]: value }
                        )
                      }
                    />
                  </Fragment>
                ))}
              </Box>
            )}
          </Stack>
          <Group position="right" pt={32}>
            <Button variant="subtle-gray" onClick={closeColumnsManager}>
              Zrušiť
            </Button>
            <Button
              variant="primary"
              type="submit"
              onClick={() => {
                setHiddenColumns(columnsManagerHidden);
                setColumnSizes(columnsManagerWidths);

                applyProfile({
                  ...currentProfile.current,
                  hiddenColumns: columnsManagerHidden,
                  filters: columnsManagerFilters,
                  columnSizes: columnsManagerWidths,
                });

                closeColumnsManager();
              }}
            >
              Potvrdiť
            </Button>
          </Group>
        </Modal>
      )}
    </Stack>
  );
}

export default forwardRef(DataTable) as <TData>(
  props: DataTableProps<TData> & { ref?: React.ForwardedRef<IDataTablePublic> }
) => ReturnType<typeof DataTable>;
